// Espace Vendeur (Lot M1) · mobile-first
// SQUELETTE volontairement minimaliste — la vue riche arrive en M8.
// Vérifie la session vendeur, affiche l'entête mandat + placeholder sections.

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileSignature, LogOut, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { sellerAuthApi } from "@/lib/api/sellerAuth";

const STATUS_LABEL = {
  draft: "Brouillon", onboarding: "En préparation",
  commercializing: "En commercialisation", reserved: "Réservé",
  archived: "Archivé",
};

const vehicleTitle = (v = {}) => {
  const bits = [v.brand, v.model, v.version].filter(Boolean);
  return bits.join(" ") || "Véhicule";
};

const fmt = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR",
      { day: "2-digit", month: "short", year: "numeric" });
  } catch { return ""; }
};

const Placeholder = ({ title, milestone, description, testId }) => (
  <div
    className="rounded-2xl bg-white/[0.04] p-5"
    data-testid={testId}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        <div className="text-[14px] font-medium text-white">{title}</div>
        {description && (
          <p className="mt-1 text-[12.5px] text-white/50">{description}</p>
        )}
      </div>
      <span className="mt-0.5 text-[9.5px] font-medium uppercase tracking-[0.16em] text-white/35">
        {milestone}
      </span>
    </div>
  </div>
);

export default function VendeurMandat() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [seller, setSeller] = useState(null);
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const me = await sellerAuthApi.me();
        setSeller(me.seller);
        const mid = id || me.mandate_id;
        if (mid) {
          const m = await sellerAuthApi.myMandate(mid);
          setMandate(m);
        }
      } catch (e) {
        if (e.status === 401) {
          navigate("/vendeur/login");
          return;
        }
        toast.error(e.message || "Erreur");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  const logout = async () => {
    try { await sellerAuthApi.logout(); } catch { /* ignore */ }
    navigate("/vendeur/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white p-6" data-testid="vendeur-mandat-loading">
        <div className="mx-auto max-w-[440px] space-y-3">
          <div className="h-8 w-24 animate-pulse rounded-full bg-white/[0.06]" />
          <div className="h-24 animate-pulse rounded-2xl bg-white/[0.04]" />
          <div className="h-16 animate-pulse rounded-2xl bg-white/[0.04]" />
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen text-white"
      style={{ background: "linear-gradient(180deg, #060606 0%, #0C0C0C 45%, #131313 100%)" }}
      data-testid="vendeur-mandat-page"
    >
      <div className="mx-auto flex min-h-screen max-w-[440px] flex-col px-6 py-8">
        {/* Header vendeur */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <FileSignature size={18} className="text-[#C8FF2F]" strokeWidth={1.7} />
            <span className="text-[12.5px] font-medium">Espace vendeur</span>
          </div>
          <button
            onClick={logout}
            className="inline-flex items-center gap-1 rounded-full bg-white/[0.05] px-3 py-1.5 text-[11.5px] text-white/60 hover:text-white"
            data-testid="vendeur-logout-btn"
          >
            <LogOut size={12} /> Quitter
          </button>
        </div>

        {/* Salutation */}
        <div className="mt-8">
          <p className="text-[11px] uppercase tracking-[0.16em] text-white/40">
            Bonjour
          </p>
          <h1 className="mt-1 text-[26px] font-semibold leading-[1.1] tracking-[-0.02em]">
            {seller?.name || "Vendeur"}
          </h1>
        </div>

        {/* Mandat hero */}
        {mandate ? (
          <section
            className="mt-8 rounded-3xl bg-white/[0.05] p-5"
            data-testid="vendeur-mandat-hero"
          >
            <div className="text-[10.5px] uppercase tracking-[0.14em] text-white/45">
              {STATUS_LABEL[mandate.status] || mandate.status}
              {" · "}
              <span className="font-mono">{mandate.reference}</span>
            </div>
            <div className="mt-3 text-[22px] font-medium leading-[1.15] tracking-[-0.01em]">
              {vehicleTitle(mandate.vehicle)}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[12.5px] text-white/55">
              {mandate.vehicle?.year && <span>{mandate.vehicle.year}</span>}
              {typeof mandate.vehicle?.mileage === "number" && (
                <span>{mandate.vehicle.mileage.toLocaleString("fr-FR")} km</span>
              )}
              {mandate.vehicle?.energy && <span>{mandate.vehicle.energy}</span>}
              {mandate.vehicle?.color && <span>{mandate.vehicle.color}</span>}
            </div>
            <div className="mt-4 text-[11px] text-white/35">
              Mis à jour {fmt(mandate.updated_at)}
            </div>
          </section>
        ) : (
          <div className="mt-8 rounded-2xl bg-white/[0.04] p-5 text-center text-[13px] text-white/50">
            Aucun mandat associé à votre compte pour le moment.
          </div>
        )}

        {/* Sections à venir M2-M8 */}
        <div className="mt-6 space-y-2.5" data-testid="vendeur-sections-placeholders">
          <Placeholder
            title="Suivi de commercialisation"
            milestone="M5"
            description="Statistiques, visites, offres et repositionnement de prix."
            testId="vendeur-placeholder-monitoring"
          />
          <Placeholder
            title="Proposition tarifaire"
            milestone="M7"
            description="Vous êtes notifié dès qu'une proposition attend votre décision."
            testId="vendeur-placeholder-proposal"
          />
          <Placeholder
            title="Échanges avec votre agence"
            milestone="M8"
            description="Messagerie contextualisée mandat par mandat."
            testId="vendeur-placeholder-messages"
          />
        </div>

        <div className="mt-auto pt-10 text-[11px] text-white/30 text-center">
          Cette version pose les fondations de votre espace.
          Les vues détaillées arrivent progressivement.
        </div>
      </div>
    </div>
  );
}
